import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import Select from 'react-select';
import { userAPI } from '../../services/api';
import { hasDefaultProfileName, requiresProfileCompletion } from '../../utils/profileUtils';
import { showSuccessToast, showErrorToast } from '../../utils/notificationUtils';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const ProfileNameRequiredModal = ({ user, onProfileUpdated }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const firstInputRef = useRef(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [countryId, setCountryId] = useState('');
  const [cityId, setCityId] = useState('');
  const [countries, setCountries] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const needsName = !!user && hasDefaultProfileName(user);
  const needsCountry = !!user && !(user.country_id || user.country);
  const needsCity = !!user && !(user.city_id || user.city);
  const isOpen = !!user && requiresProfileCompletion(user);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setFirstName('');
      setLastName('');
      setCountryId(user.country_id || user.country || '');
      setCityId(user.city_id || user.city || '');
      setErrors({});
      setTimeout(() => firstInputRef.current?.focus(), 100);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, user]);

  // Fetch countries when modal opens
  useEffect(() => {
    if (!isOpen || countries.length > 0) return;

    const fetchCountries = async () => {
      setIsLoadingCountries(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/locations/countries`, {
          headers: { Accept: 'application/json' }
        });
        const result = await response.json();
        const list = result.data || result || [];
        setCountries(list);
        const options = list.map((country) => ({
          value: country.id,
          label: isRTL ? (country.name_ar || country.name_en) : country.name_en
        }));
        setCountryOptions(options);
      } catch (error) {
        console.error('Failed to load countries:', error);
      } finally {
        setIsLoadingCountries(false);
      }
    };

    fetchCountries();
  }, [isOpen, countries.length, isRTL]);

  // Fetch cities when country changes
  useEffect(() => {
    if (!isOpen || !countryId) {
      setCityOptions([]);
      return;
    }

    const fetchCities = async () => {
      setIsLoadingCities(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/locations/cities/country/${countryId}`, {
          headers: { Accept: 'application/json' }
        });
        const result = await response.json();
        const list = result.data || result || [];
        const options = list.map((city) => ({
          value: city.id,
          label: isRTL ? (city.name_ar || city.name_en) : city.name_en
        }));
        setCityOptions(options);
      } catch (error) {
        console.error('Failed to load cities:', error);
      } finally {
        setIsLoadingCities(false);
      }
    };

    fetchCities();
  }, [isOpen, countryId, isRTL]);

  if (!isOpen) return null;

  const validate = () => {
    const newErrors = {};

    if (needsName) {
      const trimmedFirst = firstName.trim();
      const trimmedLast = lastName.trim();

      if (!trimmedFirst) {
        newErrors.firstName = t('profileNameRequired.errors.firstNameRequired');
      } else if (trimmedFirst.toLowerCase() === 'john') {
        newErrors.firstName = t('profileNameRequired.errors.firstNameNotJohn');
      }

      if (!trimmedLast) {
        newErrors.lastName = t('profileNameRequired.errors.lastNameRequired');
      } else if (trimmedLast.toLowerCase() === 'doe') {
        newErrors.lastName = t('profileNameRequired.errors.lastNameNotDoe');
      }

      if (
        !newErrors.firstName &&
        !newErrors.lastName &&
        `${trimmedFirst} ${trimmedLast}`.trim().toLowerCase() === 'john doe'
      ) {
        newErrors.firstName = t('profileNameRequired.errors.notDefaultName');
        newErrors.lastName = t('profileNameRequired.errors.notDefaultName');
      }
    }

    if (needsCountry && !countryId) {
      newErrors.country = t('profileNameRequired.errors.countryRequired');
    }

    if (needsCity && !cityId) {
      newErrors.city = t('profileNameRequired.errors.cityRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    try {
      const payload = {
        profile_status: 'complete'
      };

      if (needsName) {
        payload.first_name = firstName.trim();
        payload.last_name = lastName.trim();
      }

      if (needsCountry) {
        payload.country_id = countryId;
      }

      if (needsCity) {
        payload.city_id = cityId;
      }

      const response = await userAPI.updateProfile(payload);
      const responseData = response.data?.data || response.data;

      const updatedUser = {
        ...user,
        ...responseData,
        first_name: needsName ? firstName.trim() : user.first_name,
        last_name: needsName ? lastName.trim() : user.last_name,
        country_id: needsCountry ? countryId : user.country_id,
        city_id: needsCity ? cityId : user.city_id,
        profile_status: 'complete'
      };

      localStorage.setItem('user_data', JSON.stringify(updatedUser));

      showSuccessToast(t('profileNameRequired.success'));
      onProfileUpdated?.(updatedUser);
    } catch (error) {
      console.error('Failed to update profile:', error);
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        t('profileNameRequired.errors.submitFailed');
      showErrorToast(message);
    } finally {
      setIsLoading(false);
    }
  };

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      height: '48px',
      borderRadius: '12px',
      border: `1.5px solid ${state.selectProps.error ? '#EF4444' : '#E5E7EB'}`,
      boxShadow: 'none',
      '&:hover': {
        borderColor: state.selectProps.error ? '#EF4444' : '#8548A8'
      },
      textAlign: isRTL ? 'right' : 'left'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#9CA3AF',
      textAlign: isRTL ? 'right' : 'left'
    }),
    singleValue: (base) => ({
      ...base,
      textAlign: isRTL ? 'right' : 'left'
    }),
    option: (base) => ({
      ...base,
      textAlign: isRTL ? 'right' : 'left'
    }),
    menu: (base) => ({
      ...base,
      zIndex: 10000
    })
  };

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '20px',
          padding: '32px',
          maxWidth: '460px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          fontFamily: '"Red Hat Display", sans-serif',
          direction: isRTL ? 'rtl' : 'ltr'
        }}
        dir={i18n.dir()}
        lang={i18n.language}
      >
        <div style={{ marginBottom: '24px', textAlign: isRTL ? 'right' : 'center' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              backgroundColor: '#F3E8FF',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px auto'
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#8548A8"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>

          <h2
            style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 600,
              fontSize: '22px',
              lineHeight: '1.3',
              color: '#1F2937',
              margin: '0 0 8px 0'
            }}
          >
            {t('profileNameRequired.title')}
          </h2>
          <p
            style={{
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '1.5',
              color: '#6B7280',
              margin: 0
            }}
          >
            {t('profileNameRequired.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {needsName && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#374151',
                    display: 'block',
                    marginBottom: '6px',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                >
                  {t('profileNameRequired.firstNameLabel')}
                </label>
                <input
                  ref={firstInputRef}
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder={t('profileNameRequired.firstNamePlaceholder')}
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: '0 16px',
                    border: `1.5px solid ${errors.firstName ? '#EF4444' : '#E5E7EB'}`,
                    borderRadius: '12px',
                    outline: 'none',
                    fontSize: '14px',
                    fontFamily: '"Red Hat Display", sans-serif',
                    textAlign: isRTL ? 'right' : 'left',
                    boxSizing: 'border-box'
                  }}
                />
                {errors.firstName && (
                  <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', marginBottom: 0, textAlign: isRTL ? 'right' : 'left' }}>
                    {errors.firstName}
                  </p>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#374151',
                    display: 'block',
                    marginBottom: '6px',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                >
                  {t('profileNameRequired.lastNameLabel')}
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder={t('profileNameRequired.lastNamePlaceholder')}
                  disabled={isLoading}
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: '0 16px',
                    border: `1.5px solid ${errors.lastName ? '#EF4444' : '#E5E7EB'}`,
                    borderRadius: '12px',
                    outline: 'none',
                    fontSize: '14px',
                    fontFamily: '"Red Hat Display", sans-serif',
                    textAlign: isRTL ? 'right' : 'left',
                    boxSizing: 'border-box'
                  }}
                />
                {errors.lastName && (
                  <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', marginBottom: 0, textAlign: isRTL ? 'right' : 'left' }}>
                    {errors.lastName}
                  </p>
                )}
              </div>
            </>
          )}

          {needsCountry && (
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: '#374151',
                  display: 'block',
                  marginBottom: '6px',
                  textAlign: isRTL ? 'right' : 'left'
                }}
              >
                {t('profileNameRequired.countryLabel')}
              </label>
              <Select
                options={countryOptions}
                value={countryOptions.find((opt) => String(opt.value) === String(countryId)) || null}
                onChange={(selected) => {
                  setCountryId(selected ? selected.value : '');
                  setCityId('');
                  setErrors((prev) => ({ ...prev, country: '', city: '' }));
                }}
                placeholder={t('profileNameRequired.countryPlaceholder')}
                isDisabled={isLoading || isLoadingCountries}
                isLoading={isLoadingCountries}
                error={!!errors.country}
                styles={customSelectStyles}
              />
              {errors.country && (
                <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', marginBottom: 0, textAlign: isRTL ? 'right' : 'left' }}>
                  {errors.country}
                </p>
              )}
            </div>
          )}

          {needsCity && (
            <div style={{ marginBottom: '24px' }}>
              <label
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: '#374151',
                  display: 'block',
                  marginBottom: '6px',
                  textAlign: isRTL ? 'right' : 'left'
                }}
              >
                {t('profileNameRequired.cityLabel')}
              </label>
              <Select
                options={cityOptions}
                value={cityOptions.find((opt) => String(opt.value) === String(cityId)) || null}
                onChange={(selected) => {
                  setCityId(selected ? selected.value : '');
                  setErrors((prev) => ({ ...prev, city: '' }));
                }}
                placeholder={t('profileNameRequired.cityPlaceholder')}
                isDisabled={isLoading || !countryId || isLoadingCities}
                isLoading={isLoadingCities}
                error={!!errors.city}
                styles={customSelectStyles}
              />
              {errors.city && (
                <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px', marginBottom: 0, textAlign: isRTL ? 'right' : 'left' }}>
                  {errors.city}
                </p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              height: '48px',
              background: isLoading ? '#C4B5FD' : '#8548A8',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '12px',
              fontFamily: '"Red Hat Display", sans-serif',
              fontWeight: 600,
              fontSize: '15px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                {t('profileNameRequired.saving')}
              </>
            ) : (
              t('profileNameRequired.save')
            )}
          </button>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ProfileNameRequiredModal;
